// Shuffle — rotating-partners bet. GolfGamesScorecard's setup pre-computes a
// partner-rotation schedule and its scoring engine routes 'shuffle' to
// calcStroke (the money is tracked live in the UI, no canonical engine). We
// mirror that: calcStroke standings (imported) + the rotation schedule, plus a
// deterministic per-segment best-ball settlement as a faithful sandbox model.
export { calcStroke } from './ggStrokePlay.js'

/** 4-player rotation: three 6-hole segments, each a distinct 2v2 pairing. */
export function shuffleRotation(playerIds) {
  const [a, b, c, d] = playerIds
  return [
    { holes: '1–6', teams: [[a, b], [c, d]] },
    { holes: '7–12', teams: [[a, c], [b, d]] },
    { holes: '13–18', teams: [[a, d], [b, c]] },
  ]
}

/** Per-segment team best-ball total (min of the pair per hole); lower wins. */
export function computeShuffleSegments(players, rotation) {
  const byId = Object.fromEntries((players || []).map((p) => [p.id, p]))
  return (rotation || []).map((seg, i) => {
    const start = i * 6
    const teamTotal = (pair) => {
      let t = 0
      for (let h = start; h < start + 6; h++) {
        const vals = pair.map((id) => byId[id]?.scores[h]).filter((s) => s != null)
        if (vals.length) t += Math.min(...vals)
      }
      return t
    }
    const [A, B] = seg.teams
    const aTotal = teamTotal(A)
    const bTotal = teamTotal(B)
    return { holes: seg.holes, teams: seg.teams, aTotal, bTotal, winner: aTotal < bTotal ? 'A' : bTotal < aTotal ? 'B' : 'tie' }
  })
}

// Zero-sum settle of segment winners: each winning-team player +amt, each
// losing-team player −amt; ties pay nothing.
function settleSegments(winners, rotation, dollarsPerSegment) {
  const payouts = {}
  const amt = Number(dollarsPerSegment) || 0
  ;(rotation || []).forEach((seg) => seg.teams.flat().forEach((id) => { if (payouts[id] == null) payouts[id] = 0 }))
  ;(winners || []).forEach((w, i) => {
    const seg = rotation[i]
    if (!seg || w === 'tie' || !w || amt <= 0) return
    const win = w === 'A' ? seg.teams[0] : seg.teams[1]
    const lose = w === 'A' ? seg.teams[1] : seg.teams[0]
    win.forEach((id) => { payouts[id] += amt })
    lose.forEach((id) => { payouts[id] -= amt })
  })
  return payouts
}

// ── Auto-settle (ON): segments from scores + the defined rotation ────────────
export function autoShuffle(players, rotation, dollarsPerSegment = 0) {
  const segments = computeShuffleSegments(players, rotation)
  const payouts = settleSegments(segments.map((s) => s.winner), rotation, dollarsPerSegment)
  return { segments, payouts }
}

// ── Manual (OFF, fallback): settle from segment winners you tracked ──────────
export function manualShuffle(segmentWinners, rotation, dollarsPerSegment = 0) {
  return { payouts: settleSegments(segmentWinners, rotation, dollarsPerSegment) }
}
